<template>
  <div class="payout-section">
    <f7-block-title>
      {{ $t('payout.title') }} ({{ groupPayouts.length }})
    </f7-block-title>

    <f7-block v-if="loading" class="payout-loading">
      <f7-preloader size="28" />
    </f7-block>

    <f7-block v-else-if="!groupPayouts.length" class="payout-empty">
      <f7-icon f7="paperplane" size="22" />
      <div>{{ $t('payout.empty') }}</div>
    </f7-block>

    <f7-list v-else inset>
      <f7-list-item
        v-for="p in groupPayouts"
        :key="p.id"
        link
        @click="openDetail(p)"
      >
        <div class="payout-row">
          <div class="payout-main">
            <span class="payout-amount">{{ fmtAmount(p) }}</span>
            <span class="payout-recipient">{{ $t('payout.for', { name: p.recipientName }) }}</span>
          </div>
          <div class="payout-side">
            <PayoutStatusPill :status="p.status" />
            <span class="payout-time">{{ relativeTime(p.createdAt) }}</span>
          </div>
        </div>
      </f7-list-item>
    </f7-list>

    <f7-block v-if="canManage && !closed">
      <f7-button large raised round fill @click="initiateOpen = true">
        {{ $t('payout.requestNew') }}
      </f7-button>
    </f7-block>

    <PayoutDetailSheet
      v-model:opened="detailOpen"
      :payout="detailPayout"
      :viewer-id="viewerId"
      :check-auth="checkAuth"
      @updated="onUpdated"
    />

    <PayoutInitiateSheet
      v-model:opened="initiateOpen"
      :group-id="groupId"
      :campaign-id="campaignId"
      :members="members"
      :admin-count="adminCount"
      :viewer-id="viewerId"
      :check-auth="checkAuth"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { f7 } from 'framework7-vue'
import i18n from '../../js/i18n.js'
import PayoutStatusPill from './PayoutStatusPill.vue'
import PayoutDetailSheet from './PayoutDetailSheet.vue'
import PayoutInitiateSheet from './PayoutInitiateSheet.vue'
import { usePayoutStore } from '../../js/pinia-stores/payout.js'

const props = defineProps({
  groupId: { type: String, required: true },
  campaignId: { type: String, default: null },
  members: { type: Array, default: () => [] },
  adminCount: { type: Number, default: 0 },
  viewerId: { type: String, required: true },
  canManage: { type: Boolean, default: false },
  closed: { type: Boolean, default: false },
  checkAuth: { type: Function, default: null },
})

const payoutStore = usePayoutStore()
const groupPayouts = payoutStore.groupPayouts
const loading = payoutStore.loading

const detailPayout = ref(null)
const detailOpen = ref(false)
const initiateOpen = ref(false)

async function load() {
  try {
    await payoutStore.fetchForGroup(props.groupId)
  } catch (e) {
    f7.toast.show({ text: e?.response?.data?.message || i18n.global.t('payout.loadFailed'), closeTimeout: 2000 })
  }
}

async function openDetail(p) {
  f7.preloader.show()
  try {
    detailPayout.value = await payoutStore.fetchPayout(p.id)
    detailOpen.value = true
  } catch (e) {
    f7.dialog.alert(e?.response?.data?.message || i18n.global.t('payout.loadFailed'))
  } finally {
    f7.preloader.hide()
  }
}

function onUpdated(fresh) {
  const i = groupPayouts.value.findIndex((x) => x.id === fresh.id)
  if (i !== -1) groupPayouts.value[i] = fresh
  else groupPayouts.value.unshift(fresh)
}

function fmtAmount(p) {
  return `${Number(p.amount ?? 0).toLocaleString('fr-FR')} ${p.currency || ''}`
}

function relativeTime(iso) {
  if (!iso) return ''
  const mins = Math.floor((Date.now() - new Date(iso)) / 60000)
  if (mins < 60) return `${mins} min`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}j`
}

watch(() => props.groupId, () => load())

onMounted(load)
</script>

<style scoped>
.payout-loading, .payout-empty {
  text-align: center;
  padding: 20px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.payout-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
}
.payout-main { display: flex; flex-direction: column; min-width: 0; }
.payout-amount { font-size: 14px; font-weight: 600; }
.payout-recipient { font-size: 11.5px; color: var(--f7-on-surface-variant, #8e8e93); }
.payout-side { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }
.payout-time { font-size: 10.5px; color: var(--f7-on-surface-variant, #8e8e93); }
</style>
